/* eslint-disable react/jsx-no-constructed-context-values */
import App from 'next/app';
import { useRouter } from 'next/router';
import axios from 'axios';
import { useEffect, useState } from 'react';
import {
    AckoMainTheme,
    ThemeProvider,
} from '@acko-tech/building-blocks.ui.theme';
import isMobile from 'ismobilejs';
import Hamburger from 'components/hamburger';
import { ViewportProvider } from 'utils/responsive';
import { initGlobals } from 'utils/globals';
import { MPosContext } from 'utils/context';
import { getDomainOrigin, getHeaders } from 'utils/serverConfig';
import ErrorPage from 'widgets/errorScreen';
import SnackBar from 'library/snackbar';
import '../styles/globals.css';
import {
    NewPlanStages,
    policyDetailsInitState,
} from 'components/newPlan/constants';
import { identifyEvent } from '@acko-tech/building-blocks.ui.acko-analytics';
import { PageLoader } from 'utils/styles';
import { getImageBasePath } from 'utils/generics';
import {
    autoLobPartners,
    autoPartnershipPartners,
    Partners,
} from 'utils/constants';
import { callValidateGSTDetails } from 'utils/api/services';
import { ValidateGSTDetailsResponse } from 'utils/api/types';
import { initialiseAckoAnalytics } from 'utils/globals/initialiseAckoAnalytics';
import { setTrackerIdCookie } from 'utils/cookie';

declare global {
    interface Window {
        lob?: string;
    }
}
function MPosApp(props: any) {
    const { Component, pageProps, isMobileDevice, query, host, cookie } = props;

    const router = useRouter();

    const [userDetails, setUserDetails] = useState({
        user_id: '',
        address: '',
        email: '',
        name: '',
        partner_id: '',
        phone: '',
        pincode: '',
    });
    const [permissions, setPermissions] = useState({
        group: '',
        partner_id: '',
        partner_logo: '',
        permission_list: [],
        role: '',
        purchase_journey: 'v0',
    });
    const [metaData, setMetaData] = useState({
        promoter_id_visibility: false,
    });
    const [dealerType, setDealerType] = useState('');
    const [partnerId, setPartnerId] = useState('');
    const [permissionsMap, setPermissionsMap] = useState({});
    const [showHamburger, setShowHamburger] = useState(false);
    const [showErrorScreen, setShowErrorScreen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });
    const [stage, setActiveStage] = useState(NewPlanStages.MODEL);
    const [planDetails, setPlanDetails] = useState(policyDetailsInitState);
    const [legalDetailsUploadedStatus, setLegalDetailsUploadedStatus] =
        useState(false);
    const [loggedUserRole, setLoggedUserRole] = useState('others');
    const [errorMessage, setErrorMessage] = useState('');
    const [gstDetailsValid, setGstDetailsValid] = useState(true);
    const [gstDetailsSubmitAction, setGstDetailsSubmitAction] = useState(true);

    useEffect(() => {
        initialiseAckoAnalytics();
    }, [router?.pathname]);

    useEffect(() => {
        if (userDetails.user_id) {
            /**
             * identify segment event call
             */
            identifyEvent(userDetails.user_id, {
                phone: userDetails.phone,
            });
        }
    }, [userDetails]);

    useEffect(() => {
        if (
            router.pathname.includes('login') ||
            router.pathname.includes('logout')
        ) {
            return;
        }
        if (
            Array.isArray(router.query.slug) &&
            router.query?.slug?.length &&
            userDetails?.partner_id
        ) {
            if (
                legalDetailsUploadedStatus &&
                userDetails.partner_id === Partners.OPPO &&
                loggedUserRole === 'dealer'
            ) {
                router.push(
                    `/embedded/${userDetails.partner_id}/legal-documents`
                );
            }
        }
    }, [
        JSON.stringify(router.query.slug),
        legalDetailsUploadedStatus,
        loggedUserRole,
    ]);

    useEffect(() => {
        async function getUserPermission() {
            try {
                const { status, data } = await axios({
                    method: 'GET',
                    url: `${getDomainOrigin()}/api/mpos/user-permissions`,
                    headers: getHeaders(),
                });
                if (status === 200 && data?.permissions) {
                    const { permissions, user_details, meta_data } = data;
                    setPermissions(permissions);
                    setUserDetails(user_details);
                    setMetaData(meta_data);
                    setPartnerId(user_details?.partner_id);
                    setShowErrorScreen(false);
                    const permissionsMap = {};
                    permissions?.permission_list?.forEach(
                        (permission: string) => {
                            permissionsMap[permission] = true;
                        }
                    );
                    setPermissionsMap(permissionsMap);
                    setDealerType(permissions?.role || '');

                    let lobPrefix;
                    if (permissions?.lob) {
                        lobPrefix = permissions.lob;
                    } else {
                        lobPrefix =
                            autoLobPartners.includes(user_details.partner_id) ||
                            permissions.permission_list.includes(
                                'access_only_auto_commercial_flow'
                            )
                                ? 'auto-lob'
                                : autoPartnershipPartners.includes(
                                      user_details.partner_id
                                  )
                                ? 'auto-partnership'
                                : 'embedded';
                    }

                    if (router?.query?.slug?.includes('thankyou')) {
                        return;
                    }
                    const user_role = permissions.group;

                    try {
                        const { data, status } = await axios({
                            method: 'GET',
                            url: `${getDomainOrigin()}/api/mpos/${
                                user_details.partner_id
                            }/financial-details/exists`,
                            headers: getHeaders(),
                        });
                        if (status === 200 && data) {
                            const {
                                details_required,
                                details_present,
                                can_submit,
                            } = data;
                            setLoggedUserRole(user_role);
                            if (details_required && !details_present) {
                                setLegalDetailsUploadedStatus(true);
                                if (can_submit) {
                                    setActiveStage(
                                        NewPlanStages.LEGAL_DOCUMENTS_UPLOAD
                                    );

                                    router.push(
                                        `/${lobPrefix}/${user_details.partner_id}/legal-documents`
                                    );
                                    return;
                                }
                                if (user_role === 'sub_dealer') {
                                    router.push(
                                        `/${lobPrefix}/${user_details.partner_id}/dashboard`
                                    );
                                    return;
                                }
                            }

                            if (
                                permissions.permission_list.includes(
                                    'access_only_auto_commercial_flow'
                                )
                            ) {
                                router.push(
                                    `/${lobPrefix}/${user_details.partner_id}/form`
                                );
                                return;
                            }

                            const gstValidationResponse: ValidateGSTDetailsResponse =
                                await callValidateGSTDetails(
                                    user_details.partner_id,
                                    true
                                );

                            if (!gstValidationResponse.is_error) {
                                if (gstValidationResponse.data) {
                                    const { required, valid, can_submit } =
                                        gstValidationResponse.data;
                                    if (required && !valid) {
                                        setGstDetailsValid(false);
                                    } else {
                                        setGstDetailsValid(true);
                                    }
                                    setGstDetailsSubmitAction(can_submit);
                                }
                            } else {
                                setSnackbar({
                                    type: 'failure',
                                    message: 'Failed to validate GST Details',
                                });
                            }

                            router.push(
                                `/${lobPrefix}/${user_details.partner_id}/dashboard`
                            );
                            return;
                        }
                    } catch (e: any) {
                        if (e?.response?.status === 401) {
                            router.push('/login');
                            return;
                        }
                        if (e?.response?.status === 404) {
                            setErrorMessage(e?.response?.data?.message);
                        }
                        setShowErrorScreen(true);
                    }
                }
            } catch (e: any) {
                if (e?.response?.status === 401) {
                    setSnackbar({
                        type: 'failure',
                        message: 'Token expired. Redirecting to login page',
                    });
                    router.push('/login');
                    return;
                }
                setShowErrorScreen(true);
            } finally {
                setIsLoading(false);
            }
        }
        if (
            router.pathname.includes('/login') ||
            router.pathname.includes('/logout')
        ) {
            setIsLoading(false);
            return;
        }

        getUserPermission();
    }, []);

    useEffect(() => {
        initGlobals();
        // Set tracker ID cookie if not present
        setTrackerIdCookie();
    }, []);

    if (isLoading) {
        return <PageLoader src={getImageBasePath('/gif/pageLoader.gif')} />;
    }

    if (showErrorScreen) {
        return <ErrorPage errorMessage={errorMessage} />;
    }

    return (
        <ThemeProvider theme={AckoMainTheme}>
            <MPosContext.Provider
                value={{
                    cookie,
                    partnerId,
                    userDetails,
                    permissions,
                    metaData,
                    dealerType,
                    showHamburger,
                    permissionsMap,
                    activeStage: stage,
                    planDetails,
                    legalDetailsUploadedStatus,
                    loggedUserRole,
                    gstDetailsValid,
                    gstDetailsSubmitAction,
                    setPlanDetails,
                    setActiveStage,
                    setPermissions,
                    setUserDetails,
                    setPartnerId,
                    setShowHamburger,
                    setSnackbar,
                    setLoggedUserRole,
                    setLegalDetailsUploadedStatus,
                    setGstDetailsValid,
                    setMetaData,
                    setPermissionsMap,
                    setGstDetailsSubmitAction,
                }}
            >
                <ViewportProvider isMobileDevice={isMobileDevice}>
                    {showHamburger ? (
                        <Hamburger />
                    ) : (
                        <>
                            <Component
                                {...pageProps}
                                query={query}
                                host={host}
                                cookie={cookie}
                            />
                            <SnackBar
                                {...snackbar}
                                handleSnakbarComplete={() =>
                                    setSnackbar({ type: '', message: '' })
                                }
                            />
                        </>
                    )}
                </ViewportProvider>
            </MPosContext.Provider>
        </ThemeProvider>
    );
}

MPosApp.getInitialProps = async (context: any) => {
    const appProps = await App.getInitialProps(context);
    const userAgent =
        typeof window === 'undefined'
            ? context?.ctx?.req?.headers['user-agent']
            : window?.navigator?.userAgent;

    const isMobileDevice = isMobile(userAgent).any;

    return {
        ...appProps,
        isMobileDevice,
        query: context?.ctx?.query,
        host: context?.ctx?.req?.headers?.host,
        cookie: context?.ctx?.req?.headers?.cookie || '',
    };
};

export default MPosApp;
